import { useState, useEffect } from "react";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageContent } from "../components/PageContent";
import { Card } from "../components/Card";
import { Button } from "../components/Button";
import { LoadingContainer } from "../components/LoadingContainer";
import { InfoBox } from "../components/InfoBox";
import { SidebarPanel } from "../components/SidebarPanel";
import { FormGroup } from "../components/FormGroup";
import { FormLabel } from "../components/FormLabel";
import { SelectInput } from "../components/SelectInput";
import { IntegrationForm } from "./components/IntegrationForm";
import { IntegrationType } from "@KassaroAB/shared/types/enums/IntegrationType";
import { Features } from "@KassaroAB/shared/types/enums/Features";
import { PaymentProvider } from "@KassaroAB/shared/types/enums/PaymentProvider";
import {
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
} from "../components/Table";
import { formatDate } from "../utils/dateUtils";
import {
  useGetIntegrationsQuery,
  useDeleteIntegrationMutation,
} from "../query/integrationsQuery";
import { useGetStoreQuery } from "../query/storesQuery";
import { useGetEntitlementsQuery } from "../query/entitlementsQuery";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import type { RestIntegration } from "@KassaroAB/shared/types/integrations/integration";
import type { Entitlements } from "@KassaroAB/shared/types/entitlements";
import type { StoreId, IntegrationId } from "@KassaroAB/shared/types/ids";

const INTEGRATION_TYPE_OPTIONS = [
  { value: IntegrationType.WESTPAY_CLOUD, labelKey: "integrationsView.type.WESTPAY_CLOUD" },
  { value: IntegrationType.VIVA, labelKey: "integrationsView.type.VIVA" },
  { value: IntegrationType.ELAVON, labelKey: "integrationsView.type.ELAVON" },
  { value: IntegrationType.PAYTRIM, labelKey: "integrationsView.type.PAYTRIM" },
  { value: IntegrationType.SOFTPAY, labelKey: "integrationsView.type.SOFTPAY" },
  { value: IntegrationType.NETS, labelKey: "integrationsView.type.NETS" },
  { value: IntegrationType.MARKETPAY, labelKey: "integrationsView.type.MARKETPAY" },
  { value: IntegrationType.FORTNOX, labelKey: "integrationsView.type.FORTNOX" },
  { value: IntegrationType.SPIRIS, labelKey: "integrationsView.type.SPIRIS" },
  { value: IntegrationType.SHOPIFY, labelKey: "integrationsView.type.SHOPIFY" },
  { value: IntegrationType.PERSONALKOLLEN, labelKey: "integrationsView.type.PERSONALKOLLEN" },
] as const;

const hasPaymentTerminal = (
  entitlements: Entitlements,
  paymentProvider: PaymentProvider
): boolean =>
  (entitlements.paymentTerminals[paymentProvider] ?? 0) > 0 ||
  entitlements.licensedPaymentProviders.includes(paymentProvider);

const isIntegrationTypeAvailable = (
  integrationType: IntegrationType,
  entitlements: Entitlements | undefined
): boolean => {
  if (!entitlements) {
    return false;
  }
  if (integrationType === IntegrationType.VIVA) {
    return true;
  }
  if (
    integrationType === IntegrationType.ELAVON ||
    integrationType === IntegrationType.PAYTRIM
  ) {
    return true;
  }
  if (
    integrationType === IntegrationType.FORTNOX ||
    integrationType === IntegrationType.SPIRIS
  ) {
    return entitlements.features.includes(Features.AUTOMATIC_BOOKKEEPING);
  }
  if (integrationType === IntegrationType.WESTPAY_CLOUD) {
    return hasPaymentTerminal(entitlements, PaymentProvider.WESTPAY);
  }
  if (integrationType === IntegrationType.SOFTPAY) {
    return hasPaymentTerminal(entitlements, PaymentProvider.SOFTPAY);
  }
  if (integrationType === IntegrationType.MARKETPAY) {
    return hasPaymentTerminal(entitlements, PaymentProvider.MARKETPAY);
  }
  if (integrationType === IntegrationType.NETS) {
    return hasPaymentTerminal(entitlements, PaymentProvider.NETS);
  }
  if (integrationType === IntegrationType.SHOPIFY) {
    return entitlements.features.includes(Features.PRODUCT_SYNC);
  }
  if (integrationType === IntegrationType.PERSONALKOLLEN) {
    return entitlements.features.includes(Features.EXTERNAL_INTEGRATIONS);
  }
  return false;
};

const isBoundToOtherStore = (
  integration: RestIntegration,
  storeId: StoreId | undefined
): boolean =>
  integration.type === IntegrationType.PERSONALKOLLEN &&
  Boolean(integration.storeId) &&
  integration.storeId !== storeId;

const DeleteButton = styled(Button)`
  background-color: ${colors.danger};
  padding: ${spacing.x1}px ${spacing.x2}px;
  font-size: ${fontSizes.small}px;
`;

export const IntegrationsView = () => {
  const { t } = useTranslation();
  const { storeId } = useParams<{ storeId: StoreId }>();
  const [selectedIntegrationId, setSelectedIntegrationId] = useState<IntegrationId | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [formActionError, setFormActionError] = useState(false);
  const [createIntegrationType, setCreateIntegrationType] = useState("");

  const { data: store, isLoading: isLoadingStore } = useGetStoreQuery(storeId);
  const {
    data: chainIntegrations,
    isLoading: isLoadingIntegrations,
    error: integrationsError,
  } = useGetIntegrationsQuery(store?.chainId ?? null);
  const { data: entitlements, isLoading: isLoadingEntitlements } =
    useGetEntitlementsQuery(storeId ?? null);

  const { mutate: deleteMutation, isError: isDeleteError } = useDeleteIntegrationMutation(store?.chainId ?? null);

  const integrations = chainIntegrations?.filter(
    (item) => !isBoundToOtherStore(item, storeId)
  );
  const selectedIntegration = integrations?.find((item) => item.id === selectedIntegrationId);
  const availableIntegrationTypes = INTEGRATION_TYPE_OPTIONS.filter((option) => {
    return isIntegrationTypeAvailable(option.value, entitlements);
  });

  const handleIntegrationClick = (item: RestIntegration) => {
    setFormActionError(false);
    setSelectedIntegrationId(item.id);
    setIsCreating(false);
    setIsPanelOpen(true);
  };

  const handleCreateClick = () => {
    setFormActionError(false);
    setSelectedIntegrationId(null);
    setCreateIntegrationType("");
    setIsCreating(true);
    setIsPanelOpen(true);
  };

  const handleFormSuccess = () => {
    setFormActionError(false);
    setSuccessMessage(
      isCreating ? t("integrationsView.successCreated") : t("integrationsView.successUpdated"),
    );
    setIsPanelOpen(false);
    setIsCreating(false);
    setSelectedIntegrationId(null);
    setCreateIntegrationType("");
  };

  const handleFormError = () => {
    setSuccessMessage(null);
    setFormActionError(true);
  };

  const handleDelete = (itemId: IntegrationId, e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm(t("integrationsView.deleteConfirm"))) {
      deleteMutation(itemId, {
        onSuccess: () => {
          setFormActionError(false);
          setSuccessMessage(t("integrationsView.successDeleted"));
        },
        onError: () => {
          setSuccessMessage(null);
          setFormActionError(true);
        },
      });
    }
  };

  const handlePanelClose = () => {
    setIsPanelOpen(false);
    setIsCreating(false);
    setSelectedIntegrationId(null);
    setCreateIntegrationType("");
    setFormActionError(false);
  };

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const resolvedCreateIntegrationType =
    createIntegrationType &&
    isIntegrationTypeAvailable(
      createIntegrationType as IntegrationType,
      entitlements
    )
      ? createIntegrationType
      : "";

  if (isLoadingStore || isLoadingIntegrations || isLoadingEntitlements) {
    return (
      <PageContent title={t("integrationsView.title")}>
        <LoadingContainer message={t("integrationsView.loading")} />
      </PageContent>
    );
  }

  return (
    <PageContent
      title={t("integrationsView.title")}
      actions={
        <Button
          onClick={handleCreateClick}
          disabled={availableIntegrationTypes.length === 0}
        >
          {t("integrationsView.create")}
        </Button>
      }
    >
      {integrationsError && (
        <InfoBox variant="error">
          {t("integrationsView.errorLoad")}
        </InfoBox>
      )}

      {(formActionError || isDeleteError) && (
        <InfoBox variant="error">
          {t("integrationsView.errorUpdate")}
        </InfoBox>
      )}

      {successMessage && (
        <InfoBox variant="success">{successMessage}</InfoBox>
      )}

      {integrations && integrations.length > 0 ? (
        <Card>
          <Table>
            <TableHeader>
              <tr>
                <TableHeaderCell>{t("integrationsView.tableType")}</TableHeaderCell>
                <TableHeaderCell>{t("integrationsView.tableName")}</TableHeaderCell>
                <TableHeaderCell>{t("integrationsView.tableCreated")}</TableHeaderCell>
                <TableHeaderCell>{t("integrationsView.tableUpdated")}</TableHeaderCell>
                <TableHeaderCell>{t("integrationsView.tableActions")}</TableHeaderCell>
              </tr>
            </TableHeader>
            <TableBody>
              {integrations.map((item) => (
                <TableRow
                  key={item.id}
                  clickable
                  onClick={() => handleIntegrationClick(item)}
                >
                  <TableCell noWrap>{item.type}</TableCell>
                  <TableCell noWrap>{item.name}</TableCell>
                  <TableCell noWrap>{"createdAt" in item ? formatDate(item.createdAt as string) : "-"}</TableCell>
                  <TableCell noWrap>{"updatedAt" in item ? formatDate(item.updatedAt as string) : "-"}</TableCell>
                  <TableCell noWrap>
                    <DeleteButton onClick={(e) => handleDelete(item.id, e)}>
                      {t("integrationsView.delete")}
                    </DeleteButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <Card>
          <div style={{ textAlign: "center", color: colors.textMuted }}>
            {t("integrationsView.empty")}
          </div>
        </Card>
      )}

      {(selectedIntegration || isCreating) && storeId && (
        <SidebarPanel
          isOpen={isPanelOpen}
          onClose={handlePanelClose}
          title={
            isCreating
              ? t("integrationsView.panelCreate")
              : t("integrationsView.panelEdit", { name: selectedIntegration?.name ?? "" })
          }
        >
          {isCreating && (
            <FormGroup>
              <FormLabel>{t("integrationForm.typeLabel")}</FormLabel>
              {availableIntegrationTypes.length === 0 ? (
                <InfoBox variant="info">
                  {t("integrationsView.noAvailableTypes")}
                </InfoBox>
              ) : (
                <SelectInput
                  value={resolvedCreateIntegrationType}
                  onChange={(e) => setCreateIntegrationType(e.target.value)}
                  required
                >
                  <option value="">{t("integrationForm.selectPlaceholder")}</option>
                  {availableIntegrationTypes.map((option) => (
                    <option key={option.value} value={option.value}>
                      {t(option.labelKey)}
                    </option>
                  ))}
                </SelectInput>
              )}
            </FormGroup>
          )}
          <IntegrationForm
            storeId={storeId}
            chainId={store?.chainId}
            integration={selectedIntegration || undefined}
            integrationType={
              isCreating ? resolvedCreateIntegrationType : undefined
            }
            onSuccess={handleFormSuccess}
            onError={handleFormError}
            onCancel={handlePanelClose}
          />
        </SidebarPanel>
      )}
    </PageContent>
  );
};
